import { isInternalEmail } from './internalAccounts';

/**
 * Every traveler on a group trip gets their own trip row pointing at the group
 * (master) trip through masterTripId. The admin list shows one row per group,
 * with the traveler trips nested under it as `travelerTrips`.
 */

const SEARCH_FIELDS = ['title', 'destination', 'creatorName', 'creatorEmail', 'partnerName', 'id'];

const tripMatchesSearch = (trip, search) => (
  SEARCH_FIELDS.some((field) => String(trip?.[field] ?? '').toLowerCase().includes(search))
);

const isInternalTrip = (trip) => isInternalEmail(trip?.creatorEmail);

const sortByCreatedAsc = (trips) => [...trips].sort(
  (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
);

const toGroupRow = (master, travelerTrips) => ({
  ...master,
  isMasterTrip: true,
  memberCount: travelerTrips.length || master.memberCount,
  travelerTrips,
});

/**
 * @param {Array} trips flat list from /admin/trips
 * @returns {Array} one row per standalone trip or group; groups carry `travelerTrips`
 */
export const groupAdminTrips = (trips) => {
  const list = trips || [];
  const travelersByMaster = new Map();
  list.forEach((trip) => {
    if (trip?.masterTripId == null) return;
    const key = String(trip.masterTripId);
    if (!travelersByMaster.has(key)) travelersByMaster.set(key, []);
    travelersByMaster.get(key).push(trip);
  });

  const rows = [];
  const placedGroups = new Set();
  list.forEach((trip) => {
    if (trip?.masterTripId != null) return;
    const key = String(trip.id);
    if (trip.isMasterTrip || travelersByMaster.has(key)) {
      placedGroups.add(key);
      rows.push(toGroupRow(trip, sortByCreatedAsc(travelersByMaster.get(key) || [])));
    } else {
      rows.push({ ...trip, travelerTrips: [] });
    }
  });

  // The group trip itself can fall outside the list limit; keep its travelers together anyway.
  travelersByMaster.forEach((travelers, key) => {
    if (placedGroups.has(key)) return;
    const sorted = sortByCreatedAsc(travelers);
    rows.push(toGroupRow({ ...sorted[0], id: Number(key) || key }, sorted));
  });

  return rows;
};

export const isAdminTripGroup = (row) => Boolean(row?.isMasterTrip);

/**
 * Applies the search box and the internal-accounts switch to grouped rows. A
 * group stays while any of its trips survives, so a group run from a staff
 * account still shows when real travelers booked it.
 *
 * @returns {Array} rows with `travelerTrips` narrowed to visible ones and
 *   `matchedByTraveler` set when only a traveler trip matched the search
 */
export const filterAdminTripGroups = (rows, { search = '', hideInternal = false } = {}) => {
  const query = String(search || '').trim().toLowerCase();
  return (rows || []).reduce((visible, row) => {
    const travelers = hideInternal
      ? row.travelerTrips.filter((trip) => !isInternalTrip(trip))
      : row.travelerTrips;
    const rowHidden = hideInternal && isInternalTrip(row);
    if (rowHidden && travelers.length === 0) return visible;

    const rowMatches = !query || tripMatchesSearch(row, query);
    const travelerMatches = query ? travelers.some((trip) => tripMatchesSearch(trip, query)) : false;
    if (!rowMatches && !travelerMatches) return visible;

    visible.push({
      ...row,
      memberCount: isAdminTripGroup(row) && row.travelerTrips.length ? travelers.length : row.memberCount,
      travelerTrips: travelers,
      matchedByTraveler: !rowMatches && travelerMatches,
    });
    return visible;
  }, []);
};
